import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {Info} from "lucide-react";

/** 마우스를 올리고 이만큼 기다리면 ID 툴팁이 뜬다(네이티브 title 과 비슷한 체감). */
export const HOVER_DELAY_MS = 400

/**
 * 혜택 목록 혜택명 오른쪽의 (i) 버튼.
 *
 * - 클릭 → `onClick` (혜택 정보 팝업)
 * - 마우스를 올리고 `HOVER_DELAY_MS` 기다리면 **혜택 ID 가 작게** 뜬다(2026-11-03 사용자 지시 —
 *   ID 를 확인하려고 상세 화면 주소까지 들어가는 번거로움을 없애되, ID 를 목록에 상시 노출하면
 *   고객사가 이해할 수 없는 정보가 되므로 hover 에만 보인다).
 *
 * 🔴 툴팁은 `document.body` 로 **포털**한다. `GenericBoard` 의 행이 `hover:-translate-y-0.5` 라
 *    행을 hover 하는 동안 transform 이 걸리고, 그 안의 `fixed` 는 뷰포트가 아니라 행 기준으로
 *    갇힌다(`MiniMenu` 와 같은 원인, 2026-08-27). 좌표는 hover 시점의 버튼 화면 좌표로 계산.
 * 🔴 버튼은 혜택명 칸(클릭 시 편집 이동) 안에 있으므로 클릭을 `stopPropagation` 한다.
 */
const BenefitInfoButton = ({benefitId, onClick}) => {
  const [pos, setPos] = useState(null)   // null = 툴팁 닫힘
  const ref = useRef(null)
  const timer = useRef(null)

  const cancel = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
  }
  const scheduleOpen = () => {
    cancel()
    timer.current = setTimeout(() => {
      timer.current = null
      const r = ref.current?.getBoundingClientRect()
      if (!r) return
      // 🔴 버튼 **위**에 띄운다(2026-11-03 3차 지시). 아래에 띄우면 마우스 커서가 숫자를 가린다.
      //    top = 버튼 상단, 툴팁 자신의 높이만큼 translateY(-100%) 로 올린다.
      setPos({top: r.top - 4, left: r.left + r.width / 2})
    }, HOVER_DELAY_MS)
  }
  const close = () => { cancel(); setPos(null) }

  // 언마운트·스크롤 시 떠 있는 툴팁 정리(포털이라 행이 사라져도 남을 수 있다).
  useEffect(() => {
    if (!pos) return
    window.addEventListener("scroll", close, true)
    return () => window.removeEventListener("scroll", close, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos])
  useEffect(() => cancel, [])

  const tip = (
      <span role="tooltip" data-testid="benefit-id-tip"
            style={{position: "fixed", top: pos?.top, left: pos?.left, transform: "translate(-50%, -100%)", zIndex: 1000}}
            className="bg-gray-800 text-white text-[11px] leading-none rounded px-2 py-1 shadow-lg whitespace-nowrap pointer-events-none">
        ID {benefitId}
      </span>
  )

  return (
      <>
        <button ref={ref} type="button" aria-label="혜택 정보"
                className="inline-flex items-center flex-shrink-0 ml-1 text-gray-400 hover:text-blue-500"
                onMouseEnter={scheduleOpen}
                onMouseLeave={close}
                onClick={(e) => { e.stopPropagation(); close(); onClick(e) }}>
          <Info size={16} />
        </button>
        {pos && createPortal(tip, document.body)}
      </>
  )
}

export default BenefitInfoButton
